import styled from "@emotion/styled";
import Button from "@mui/material/Button";
import {
  Box,
  IconButton,
  ToggleButton,
  ButtonGroup,
  ToggleButtonGroup,
  Radio,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import { useTheme } from "../../../ThemeContext/ThemeContext";

export const SecondaryButton = styled(Button)(({ colors }) => ({
  color: colors?.primary?.main ,
  borderRadius: "2rem",
  paddingLeft: "0.8rem",
  paddingRight: "0.8rem",
  fontSize: "14px",
  fontWeight: 500,
  minWidth: "5rem",
  height: "2.5rem",
  textTransform: "none",
  ":hover": {
    backgroundColor: colors?.hover?.primary,
  },
  "&:disabled": {
    color: colors?.text?.disabled ,
    opacity: 0.38,
  },
}));

export const PrimaryButton = styled(Button)(({ colors }) => ({
  backgroundColor: colors?.primary?.main ,
  padding: "0.6rem 1.5rem",
  "&:hover": {
    backgroundColor: colors?.primary?.main ,
  },
  "&:disabled": {
    backgroundColor: colors?.background?.disabled ,
    color: colors?.text?.disabled ,
    opacity: 0.38,
  },
  borderRadius: "2rem",
  fontSize: "14px",
  fontWeight: 500,
  color: "#FFFFFF",
  minWidth: "5rem",
  height: "2.5rem",
  textTransform: "none",
}));

export const StyledButton = styled(Box)(({ colors }) => ({
  minWidth: 84,
  maxWidth: "19rem",
  height: 32,
  padding: "0.375rem 0.5rem 0.375rem 0.75rem",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 8,
  display: "inline-flex",
  backgroundColor: colors?.primary?.light ,
  borderRadius: 8,
  "&:hover": {
    cursor: "pointer",
  },
}));

export const StyledIconButton = styled(IconButton)(({ colors }) => ({
  "&:hover": {
    backgroundColor: "transparent",
  },
  "&:disabled": {
    color: colors?.text?.disabled ,
  },
  color: colors?.text?.muted ,
}));

export const StyledToggleButton = styled(ToggleButton)(({ colors }) => ({
  padding: " 0.375rem 1rem",
  textTransform: "none",
  height: "2rem",
  borderRadius: "0.5rem",
  border: `1px solid ${colors?.border?.default }`,
  color: colors?.text?.label ,
  fontSize: "0.875rem",
  fontWeight: 500,
  lineHeight: "1.25rem",
  letterSpacing: ".00625rem",

  "&.Mui-selected": {
    display: "flex",
    gap: "0.5rem",
    backgroundColor: `${colors?.primary?.light } !important`,
    color: colors?.text?.label ,
    padding: "0.375rem 1rem 0.375rem 0.5rem",
    border: "none",
    alignItems: "center",
  },

  "&.Mui-disabled": {
    color: colors?.text?.disabled,
    borderColor: colors?.text?.disabled,
    opacity: colors?.opacity?.disabled || 0.38,
  },
}));

export const StyledToggleButtonGroup = styled(
  ToggleButtonGroup
)(({ colors }) => ({
  "& .MuiToggleButton-root": {
    border: `1px solid ${colors?.border?.default } !important`,
  },
  "&.MuiToggleButtonGroup-grouped:not(:first-child):not(:last-child) ":
  {
    borderRadius: 0,
  },
  "& .MuiToggleButtonGroup-grouped:first-child": {
    borderRadius: "50px",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },

  "& .MuiToggleButtonGroup-grouped:last-child": {
    borderRadius: "50px",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
}));

export const OutlinedButton = styled(Button)(({ colors }) => ({
  textTransform: "none",
  borderRadius: "6.25rem",
  border: `1px solid ${colors?.border?.default }`,
  color: colors?.text?.muted ,
  textAlign: "center",
  padding: "0.625rem 1.5rem",
  /* material-theme/label/large */
  fontFamily: "Roboto",
  fontSize: "0.875rem",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "1.25rem" /* 142.857% */,
  letterSpacing: "0.00625rem",
  ":hover": {
    backgroundColor: colors?.hover?.primary ,
  },
  "&:disabled": {
    color: colors?.text?.disabled ,
    opacity: 0.38,
  },
}));

export const StyledRadio = styled(Radio)(({ colors }) => ({
  "&.Mui-checked": {
    color: colors?.primary?.main ,
  },
  color: colors?.text?.muted ,
}));

export const StyledFormControlLabel = styled(FormControlLabel)(({ colors }) => ({
  "& .MuiFormControlLabel-label": {
    color: colors?.text?.primary ,
  },
}));

export const StyledLabel = styled(Box)(({ colors }) => ({
  display: "flex",
  padding: "0.375rem 0.75rem",
  justifyContent: "center",
  alignItems: "center",
  gap: " 0.5rem",
  borderRadius: "0.5rem",
  border: `1px solid ${colors?.border?.default }`,
  maxWidth: "20rem"
}));

export const StyledButtonGroup = styled(ButtonGroup)(({ colors }) => ({
  "& .MuiButton-root": {
    padding: "0.5em 1em",
    fontSize: "1em",
    display: "flex",
    alignItems: "center",
    border: `1px solid ${colors?.border?.default } !important`,
  },
  "&.MuiButtonGroup-grouped:not(:first-child):not(:last-child)": {
    borderRadius: 0,
  },
  "& .MuiButtonGroup-grouped:first-child": {
    borderRadius: "50px",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  "& .MuiButtonGroup-grouped:last-child": {
    borderRadius: "50px",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  "& .MuiButton-startIcon": {
    marginRight: "0.5em",
  },
  "& .MuiButton-root:hover": {
    backgroundColor: colors?.hover?.muted ,
  },
}));

export const StyledAccordion = styled(Accordion)({
  borderRadius: "1rem",
  boxShadow: "none",
  backgroundColor: "transparent",
  "&:before": {
    display: "none",
  },
});

export const StyledAccordionSummary = styled(AccordionSummary)(({ colors }) => ({
  padding: "0.625rem 1.5rem",
  borderRadius: "6.25rem",
  border: `1px solid ${colors?.border?.default }`,
  color: colors?.primary?.main ,
  fontFamily: "Roboto",
  fontStyle: "normal",
  height: "2.5rem",
  backgroundColor: "transparent",
  minHeight: "unset !important",
  '& .MuiAccordionSummary-content': {
    margin: 0,
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: colors?.text?.muted ,
  },
  ':hover': {
    backgroundColor: colors?.hover?.primary ,
  },
}));

export const StyledAccordionDetails = styled(AccordionDetails)(({ colors }) => ({
  backgroundColor: colors?.background?.muted ,
  borderRadius: "1rem",
  borderTop: `1px solid ${colors?.border?.light }`,
}));
